import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { 
  ArrowLeftIcon,
  CalendarIcon,
  UserIcon,
  AcademicCapIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { 
  upsertTrainingPlanItem,
  getTrainingPlanItem,
  getCompetencyDefinitions,
} from "../../../../api/hse/competency";
import { PMDatePicker } from "../../../../components/utils/PMDatePicker";
import ModernEmployeeSelector from "../../../../components/utils/Selector/ModernEmployeeSelector";
import { Formik, Form } from "formik";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";
import * as Yup from "yup";

/**
 * HSE/KS Training Plan Edit - Create or edit training plan item
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyTrainingPlanEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [competencyDefinitions, setCompetencyDefinitions] = useState([]);
  const [showEmployeeSelector, setShowEmployeeSelector] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const formikRef = React.useRef(null);
  const [formData, setFormData] = useState({
    user_id: "",
    competency_definition_id: "",
    planned_date: "",
    status: "planned",
    notes: "",
  });
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: isNew ? "create" : "edit" });

  useEffect(() => {
    fetchCompetencyDefinitions();
    if (!isNew) {
      fetchTrainingPlanItem();
    }
  }, [id]);

  const fetchCompetencyDefinitions = async () => {
    try {
      const response = await getCompetencyDefinitions({ is_active: true });
      setCompetencyDefinitions(response.competency_definitions || response.definitions || []);
    } catch (err) {
      console.error("Error fetching competency definitions:", err);
      toast.error(t("Kunne ikke laste kompetansedefinisjoner"));
    }
  };

  const fetchTrainingPlanItem = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const response = await getTrainingPlanItem(id);
      const item = response.training_plan_item || response;
      
      if (!item) {
        setError(true);
        toast.error(t("Opplæringsplan ikke funnet"));
        return;
      }
      
      const employeeId = item.employee_id || item.user_id || "";
      setFormData({
        user_id: employeeId,
        competency_definition_id: item.competency_definition_id || "",
        planned_date: item.planned_date ? new Date(item.planned_date).toISOString().split('T')[0] : "",
        status: item.status || "planned",
        notes: item.notes || "",
      });
      
      // Set selected employee if we have employee info
      if (employeeId && (item.employee_name || item.employee_email)) {
        setSelectedEmployee({
          id: parseInt(employeeId),
          name: item.employee_name || "",
          displayTitle: item.employee_name || item.employee_email || "",
          email: item.employee_email || "",
        });
      }
    } catch (err) {
      console.error("Error fetching training plan item:", err);
      setError(true);
      toast.error(t("Kunne ikke laste opplæringsplan"));
    } finally {
      setLoading(false);
    }
  };

  const validationSchema = Yup.object({
    user_id: Yup.string().required(t("Ansatt er påkrevd")),
    competency_definition_id: Yup.string().required(t("Kompetanse er påkrevd")),
    planned_date: Yup.date().required(t("Planlagt dato er påkrevd")),
    status: Yup.string().required(t("Status er påkrevd")),
  });

  const handleSubmit = async (values) => {
    if (!canManage) {
      toast.error(t("Du har ikke tilgang til å opprette/redigere opplæringsplan"));
      return;
    }

    try {
      setSaving(true);
      
      const payload = {
        ...values,
        user_id: parseInt(values.user_id),
        competency_definition_id: parseInt(values.competency_definition_id),
      };
      
      if (!isNew) {
        payload.id = parseInt(id);
      }
      
      const response = await upsertTrainingPlanItem(payload);
      
      toast.success(isNew ? t("Opplæringsplan opprettet") : t("Opplæringsplan oppdatert"));
      navigate("/dashboard/hse-ks/competency/training-plan");
    } catch (err) {
      console.error("Error saving training plan item:", err);
      const errorMessage = err.response?.data?.error || err.response?.data?.errors?.[0] || err.message || t("Kunne ikke lagre opplæringsplan");
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    navigate("/dashboard/hse-ks/competency/training-plan");
    return null;
  }

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return <ResourceError errors={[t("Kunne ikke laste opplæringsplan")]} />;
  }

  const statusOptions = [
    { value: "planned", label: t("Planlagt"), icon: CalendarIcon, color: "bg-gray-100 text-gray-800" },
    { value: "in_progress", label: t("Pågående"), icon: ClockIcon, color: "bg-blue-100 text-blue-800" },
    { value: "completed", label: t("Fullført"), icon: CheckCircleIcon, color: "bg-green-100 text-green-800" },
    { value: "cancelled", label: t("Avlyst"), icon: XMarkIcon, color: "bg-red-100 text-red-800" },
  ];

  return (
    <HsePageChrome
      title={isNew ? t("Ny opplæringsplan") : t("Rediger opplæringsplan")}
      subtitle={t("Legg til eller rediger opplæring i opplæringsplanen")}
      backButton={{
        label: t("Tilbake til opplæringsplan"),
        action: () => navigate("/dashboard/hse-ks/competency/training-plan"),
        icon: ArrowLeftIcon,
      }}
    >
      <div className="mx-auto mt-8 max-w-3xl sm:px-6 lg:px-8">
        <CompetencySubnav />
        <Formik
          innerRef={formikRef}
          initialValues={formData}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => (
            <Form className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("Grunnleggende informasjon")}
                  </h3>
                  
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {/* Employee */}
                    <div>
                      <label htmlFor="user_id" className="block text-sm font-medium text-gray-700 mb-1">
                        {t("Ansatt")} <span className="text-red-500">*</span>
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowEmployeeSelector(true)}
                        className={`mt-1 block w-full rounded-md border shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-left px-3 py-2 ${
                          errors.user_id && touched.user_id
                            ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                            : "border-gray-300"
                        } ${
                          selectedEmployee || values.user_id
                            ? "bg-white text-gray-900"
                            : "bg-white text-gray-500"
                        }`}
                      >
                        {selectedEmployee ? (
                          <div className="flex items-center justify-between">
                            <span>{selectedEmployee.displayTitle || selectedEmployee.name || selectedEmployee.email}</span>
                            {selectedEmployee.email && (
                              <span className="text-xs text-gray-500 ml-2">{selectedEmployee.email}</span>
                            )}
                          </div>
                        ) : values.user_id ? (
                          <span className="text-gray-900">{t("Ansatt valgt")} (ID: {values.user_id})</span>
                        ) : (
                          <span className="text-gray-500">{t("Velg ansatt")}</span>
                        )}
                      </button>
                      {errors.user_id && touched.user_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.user_id}</p>
                      )}
                    </div>

                    {/* Competency Definition */}
                    <div>
                      <label htmlFor="competency_definition_id" className="block text-sm font-medium text-gray-700">
                        {t("Kompetanse")} <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="competency_definition_id"
                        name="competency_definition_id"
                        value={values.competency_definition_id}
                        onChange={(e) => setFieldValue("competency_definition_id", e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      >
                        <option value="">{t("Velg kompetanse")}</option>
                        {competencyDefinitions.map((def) => (
                          <option key={def.id} value={def.id}>
                            {def.name}
                            {def.type ? ` (${def.type})` : ""}
                          </option>
                        ))}
                      </select>
                      {errors.competency_definition_id && touched.competency_definition_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.competency_definition_id}</p>
                      )}
                    </div>

                    {/* Planned Date */}
                    <div>
                      <PMDatePicker
                        name="planned_date"
                        label={`${t("Planlagt dato")} *`}
                        value={values.planned_date}
                        onChange={(e) => {
                          setFieldValue("planned_date", e.target.value);
                        }}
                        errors={errors.planned_date}
                        touched={touched.planned_date}
                        placeholder={t("Velg dato")}
                      />
                    </div>

                    {/* Status */}
                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                        {t("Status")} <span className="text-red-500">*</span>
                      </label>
                      <select
                        id="status"
                        name="status"
                        value={values.status}
                        onChange={(e) => setFieldValue("status", e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      >
                        {statusOptions.map((status) => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                      {errors.status && touched.status && (
                        <p className="mt-1 text-sm text-red-600">{errors.status}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("Notater")}
                  </h3>
                  
                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                      {t("Notater")}
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={4}
                      value={values.notes}
                      onChange={(e) => setFieldValue("notes", e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      placeholder={t("Legg til notater om opplæringen...")}
                    />
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/hse-ks/competency/training-plan")}
                  className="px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t("Avbryt")}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
                >
                  {saving ? t("Lagrer...") : (isNew ? t("Opprett") : t("Oppdater"))}
                </button>
              </div>
            </Form>
          )}
        </Formik>
        
        {/* Employee Selector Modal */}
        {showEmployeeSelector && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              onClick={() => setShowEmployeeSelector(false)}
              aria-hidden="true"
            />
            <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
              <ModernEmployeeSelector
                open={showEmployeeSelector}
                selectedEmployees={selectedEmployee ? [selectedEmployee] : (formikRef.current?.values?.user_id ? [{ id: parseInt(formikRef.current.values.user_id) }] : [])}
                onChange={(selected) => {
                  if (selected.length > 0) {
                    const employee = selected[0];
                    setSelectedEmployee(employee);
                    if (formikRef.current) {
                      formikRef.current.setFieldValue("user_id", employee.id.toString());
                    }
                  } else {
                    setSelectedEmployee(null);
                    if (formikRef.current) {
                      formikRef.current.setFieldValue("user_id", "");
                    }
                  }
                  setShowEmployeeSelector(false);
                }}
                onClose={() => setShowEmployeeSelector(false)}
                multiple={false}
              />
            </div>
          </div>
        )}
      </div>
    </HsePageChrome>
  );
};

